import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StoryModel, StoryStep } from '../editor/storyModel';
import {
  parseStoryMarkdown,
  serializeStoryMarkdown,
  type StoryMarkdownDocument,
} from './storyMarkdown';
import { globalsToRecord, setGlobals as patchGlobalHeader } from './storyGlobals';

interface StoryHistory {
  document: StoryMarkdownDocument;
  past: StoryMarkdownDocument[];
  future: StoryMarkdownDocument[];
  savedMarkdown: string;
}

function cloneDocument(document: StoryMarkdownDocument): StoryMarkdownDocument {
  return {
    globalHeader: document.globalHeader,
    steps: document.steps.map((step) => ({
      ...step,
      metadata: { ...(step.metadata || {}) },
      metadataOrder: [...(step.metadataOrder || [])],
      metadataExtraLines: [...(step.metadataExtraLines || [])],
    })),
  };
}

function createStep(): StoryStep {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return {
    id: `step-${random}`,
    title: 'Neue Station',
    body: '',
    shape: 'sphere',
    color: '#fff5ee',
    scale: 0.85,
    metadata: {},
    metadataOrder: [],
    metadataExtraLines: [],
  };
}

export function useStoryModel(
  initialMarkdown: string,
  onMarkdownChange?: (markdown: string) => void,
): StoryModel {
  const [history, setHistory] = useState<StoryHistory>(() => {
    const document = parseStoryMarkdown(initialMarkdown);
    return { document, past: [], future: [], savedMarkdown: serializeStoryMarkdown(document) };
  });
  const [selectedId, setSelectedId] = useState<string | null>(history.document.steps[0]?.id ?? null);
  const onChangeRef = useRef(onMarkdownChange);
  const mountedRef = useRef(false);
  onChangeRef.current = onMarkdownChange;

  const markdown = useMemo(
    () => serializeStoryMarkdown(history.document),
    [history.document],
  );

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    onChangeRef.current?.(markdown);
  }, [markdown]);

  useEffect(() => {
    if (selectedId && history.document.steps.some((step) => step.id === selectedId)) return;
    setSelectedId(history.document.steps[0]?.id ?? null);
  }, [history.document.steps, selectedId]);

  const commit = useCallback((mutate: (document: StoryMarkdownDocument) => StoryMarkdownDocument) => {
    setHistory((current) => {
      const next = mutate(cloneDocument(current.document));
      if (serializeStoryMarkdown(next) === serializeStoryMarkdown(current.document)) return current;
      return {
        ...current,
        document: next,
        past: [...current.past, current.document],
        future: [],
      };
    });
  }, []);

  const update = useCallback((id: string, patch: Partial<StoryStep>) => {
    commit((document) => ({
      ...document,
      steps: document.steps.map((step) => step.id === id ? { ...step, ...patch, id } : step),
    }));
  }, [commit]);

  const add = useCallback((afterId?: string | null) => {
    const step = createStep();
    commit((document) => {
      const index = afterId ? document.steps.findIndex((item) => item.id === afterId) : document.steps.length - 1;
      const steps = [...document.steps];
      steps.splice(Math.max(0, index + 1), 0, step);
      return { ...document, steps };
    });
    setSelectedId(step.id);
  }, [commit]);

  const remove = useCallback((id: string) => {
    if (history.document.steps.length <= 1) return;
    const index = history.document.steps.findIndex((step) => step.id === id);
    const fallback = history.document.steps[Math.max(0, index - 1)]?.id ?? null;
    commit((document) => ({ ...document, steps: document.steps.filter((step) => step.id !== id) }));
    setSelectedId(fallback);
  }, [commit, history.document.steps]);

  const reorder = useCallback((from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    commit((document) => {
      if (from >= document.steps.length || to >= document.steps.length) return document;
      const steps = [...document.steps];
      const [moved] = steps.splice(from, 1);
      steps.splice(to, 0, moved);
      return { ...document, steps };
    });
  }, [commit]);

  const undo = useCallback(() => {
    setHistory((current) => {
      const previous = current.past[current.past.length - 1];
      if (!previous) return current;
      return {
        ...current,
        document: previous,
        past: current.past.slice(0, -1),
        future: [current.document, ...current.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory((current) => {
      const next = current.future[0];
      if (!next) return current;
      return {
        ...current,
        document: next,
        past: [...current.past, current.document],
        future: current.future.slice(1),
      };
    });
  }, []);

  const replaceMarkdown = useCallback((nextMarkdown: string) => {
    commit(() => parseStoryMarkdown(nextMarkdown));
  }, [commit]);

  const globals = useMemo(() => globalsToRecord(history.document.globalHeader), [history.document.globalHeader]);

  const setGlobals = useCallback((patch: Record<string, string | undefined>) => {
    commit((document) => ({ ...document, globalHeader: patchGlobalHeader(document.globalHeader, patch) }));
  }, [commit]);

  const markSaved = useCallback(() => {
    setHistory((current) => ({
      ...current,
      savedMarkdown: serializeStoryMarkdown(current.document),
    }));
  }, []);

  return useMemo(() => ({
    steps: history.document.steps,
    selectedId,
    select: setSelectedId,
    update,
    add,
    remove,
    reorder,
    markdown,
    replaceMarkdown,
    globals,
    globalHeader: history.document.globalHeader,
    setGlobals,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    dirty: markdown !== history.savedMarkdown,
    markSaved,
  }), [history, selectedId, update, add, remove, reorder, markdown, replaceMarkdown, globals, setGlobals, undo, redo, markSaved]);
}
